import { useEffect, useRef } from 'react';
import { STAD_WIJKEN_PER_SLUG } from './stad-wijken-data.js';
import { CITY_CONTENT } from './city-extras.jsx';
import { DEN_HAAG_WIJKEN } from './den-haag-extra.jsx';

// ─────────────────────────────────────────────────────────────────────
// StadWijkenLuxe — premium 2026-style wijken-grid voor élke stad.
// Severity bars per wijk, hover-reveal lift, IntersectionObserver
// staged reveals. Voor steden met rich data (Rotterdam): handgeschreven
// pest-tag + beschrijving + severity. Voor overige steden: auto-generated
// pest-tag (rotating) + visueel identiek.
//
// Back-compat: RotterdamWijkenLuxe blijft geëxporteerd voor bestaande
// imports — die roept gewoon StadWijkenLuxe met slug='rotterdam' aan.
// ─────────────────────────────────────────────────────────────────────

// Rich content voor steden waarvoor wij hand-written wijk-info hebben.
// Andere steden: auto-generated via wijken-pills uit stad-wijken-data.js.
const RICH_WIJKEN = {
  rotterdam: [
    { name: "Centrum / Oude Haven",        tag: "Kakkerlakken & Bedwantsen", sev: "h", sevLabel: "Hoog",  desc: "Horeca en toerisme dragen bij aan kakkerlakken in keukens en bedwantsen in hotels en Airbnb-panden.", val: 92 },
    { name: "Oude Noorden / Crooswijk",    tag: "Ratten & Muizen",           sev: "h", sevLabel: "Zeer hoog", desc: "Zwaarst getroffen wijken. Gedateerde riolering en aaneengesloten bebouwing zorgen voor aanhoudende overlast.", val: 98 },
    { name: "Mathenesse / Delfshaven",     tag: "Ratten",                    sev: "h", sevLabel: "Hoog",  desc: "Nabijheid van het Noorderkanaal en water creëert ideale rattenbiotoop in tuinen, kelders en vloeren.", val: 86 },
    { name: "Feijenoord / IJsselmonde",    tag: "Muizen",                    sev: "m", sevLabel: "Midden", desc: "Gedeelde kruipruimtes in sociale huurwoningen zorgen voor snelle verspreiding tussen appartementen.", val: 72 },
    { name: "Hillegersberg / Overschie",   tag: "Wespen & Mollen",           sev: "m", sevLabel: "Seizoen", desc: "Groene omgeving en vrijstaande woningen: meer wespennesten in spouwmuren en mollen in tuinen.", val: 60 },
    { name: "Kralingen",                   tag: "Wespen & Ratten",           sev: "m", sevLabel: "Midden", desc: "Nabijheid Kralingse Plas, ratten in tuinen, wespen in bomen en schuren tijdens het hele seizoen.", val: 68 },
    { name: "Charlois / Zuidplein",        tag: "Muizen",                    sev: "m", sevLabel: "Midden", desc: "Appartementen met muizenoverlast via spouwmuren en gedeelde kruipruimtes. Hoge dichtheid.", val: 70 },
    { name: "Pernis / Botlek / Europoort", tag: "Ratten (industrieel)",      sev: "h", sevLabel: "Industrieel", desc: "Industrieel havengebied, ratten reizen mee via internationale scheepvaart. Bedrijven primaire doelgroep.", val: 88 },
  ],
};

// Roterende pest-tags voor auto-generated wijken. Veelvoorkomende combos
// per wijk-type, zonder per-stad onderzoek. SEO-keyword voor elke wijk.
const AUTO_TAGS = [
  "Muizen & Ratten",
  "Wespen & Mollen",
  "Kakkerlakken",
  "Muizen",
  "Wespen",
  "Mieren & Spinnen",
  "Bedwantsen",
  "Ratten",
];
const AUTO_SEVS = [
  { sev: "h", label: "Hoog",   val: 88 },
  { sev: "h", label: "Hoog",   val: 82 },
  { sev: "m", label: "Midden", val: 72 },
  { sev: "m", label: "Midden", val: 66 },
  { sev: "m", label: "Midden", val: 60 },
  { sev: "l", label: "Laag",   val: 52 },
  { sev: "l", label: "Laag",   val: 46 },
  { sev: "l", label: "Laag",   val: 42 },
];

// Bouw wijken-data uit stad-wijken-data wanneer er geen rich data is.
// Deterministische ordening op slug-hash, zodat tags/severities tussen
// builds stabiel zijn (geen 'verspringende' content bij rebuild).
function buildGenericWijken(stadSlug, wijkNames) {
  if (!wijkNames || wijkNames.length === 0) return [];
  // Hash slug voor offset zodat verschillende steden andere tag-volgorde krijgen
  let h = 0;
  for (let i = 0; i < stadSlug.length; i++) h = (h * 31 + stadSlug.charCodeAt(i)) >>> 0;
  const tagOffset = h % AUTO_TAGS.length;
  return wijkNames.map((name, i) => {
    const tag = AUTO_TAGS[(tagOffset + i) % AUTO_TAGS.length];
    const sev = AUTO_SEVS[i % AUTO_SEVS.length];
    return {
      name,
      tag,
      sev: sev.sev,
      sevLabel: sev.label,
      val: sev.val,
      // Geen desc voor auto-generated wijken — cleanere visual, vermijdt
      // generic-sounding tekst. Per-wijk content komt alleen voor handmatig
      // uitgewerkte steden in RICH_WIJKEN.
      desc: null,
    };
  });
}

// Map een wijk uit CITY_CONTENT/DEN_HAAG_WIJKEN ({name,tag,desc}) naar
// het Luxe-formaat met auto-toegekende severity op basis van positie.
// Eerste twee = hoog (de "zwaarste" wijken), middenmoot = midden, rest = laag.
function enrichWijk(w, i, total) {
  const sevs = [
    { sev: "h", label: "Hoog",   val: 88 },
    { sev: "h", label: "Hoog",   val: 82 },
    { sev: "m", label: "Midden", val: 72 },
    { sev: "m", label: "Midden", val: 66 },
    { sev: "m", label: "Midden", val: 60 },
    { sev: "l", label: "Laag",   val: 52 },
    { sev: "l", label: "Laag",   val: 46 },
    { sev: "l", label: "Laag",   val: 42 },
  ];
  const sev = sevs[Math.min(i, sevs.length - 1)];
  return {
    name: w.name,
    tag: w.tag,
    desc: w.desc || null,
    sev: sev.sev,
    sevLabel: sev.label,
    val: sev.val,
  };
}

// Resolve wijken-data voor een stad volgens prioriteit:
//   1. RICH_WIJKEN[slug]  — hand-curated Luxe data (Rotterdam)
//   2. DEN_HAAG_WIJKEN    — Den Haag specifieke wijken
//   3. CITY_CONTENT[slug].wijken — andere CityExtras steden (rich tag+desc)
//   4. Generic uit STAD_WIJKEN_PER_SLUG — overige ~96 steden, auto-tag
function resolveWijken(stadSlug) {
  if (RICH_WIJKEN[stadSlug]) return RICH_WIJKEN[stadSlug];
  if (stadSlug === 'den-haag') {
    return DEN_HAAG_WIJKEN.map((w, i) => enrichWijk(w, i, DEN_HAAG_WIJKEN.length));
  }
  const cityWijken = CITY_CONTENT?.[stadSlug]?.wijken;
  if (Array.isArray(cityWijken) && cityWijken.length > 0) {
    return cityWijken.map((w, i) => enrichWijk(w, i, cityWijken.length));
  }
  return buildGenericWijken(stadSlug, STAD_WIJKEN_PER_SLUG[stadSlug] || []);
}

export function StadWijkenLuxe({ stad }) {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const items = root.querySelectorAll('[data-reveal]');
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((en, i) => {
        if (en.isIntersecting) {
          en.target.style.transitionDelay = `${(i % 4) * 0.06}s`;
          en.target.classList.add('revealed');
          obs.unobserve(en.target);
        }
      });
    }, { threshold: 0.12 });
    items.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [stad?.slug]);

  if (!stad) return null;

  const wijken = resolveWijken(stad.slug);
  if (wijken.length === 0) return null;

  const hasRichSource = RICH_WIJKEN[stad.slug]
    || stad.slug === 'den-haag'
    || (CITY_CONTENT?.[stad.slug]?.wijken?.length > 0);
  const intro = hasRichSource
    ? `Niet elke wijk heeft hetzelfde probleem. Wij kennen ${stad.naam} van binnen en van buiten.`
    : `Dagelijks actief in alle wijken van ${stad.naam}, vakkundig en met bron-aanpak per type ongedierte.`;

  return (
    <section className="rdam-wijken" ref={rootRef}>
      <div className="container">
        <div className="sh" data-reveal>
          <div className="section-tag">Per wijk in {stad.naam}</div>
          <h2>Ongedierteoverlast per <em>wijk</em></h2>
          <p>{intro}</p>
        </div>
        <div className="rdam-wijken-grid">
          {wijken.map((w) => (
            <article
              key={w.name}
              className="rdam-wijk"
              data-reveal
              style={{ '--rdam-severity': `${w.val}%` }}
            >
              <div className="rdam-wijk-head">
                <h3 className="rdam-wijk-name">{w.name}</h3>
                <span className={`rdam-wijk-sev rdam-wijk-sev-${w.sev}`}>{w.sevLabel}</span>
              </div>
              <div className="rdam-wijk-tag">{w.tag}</div>
              {w.desc && <p className="rdam-wijk-desc">{w.desc}</p>}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// Back-compat alias: bestaande imports van RotterdamWijkenLuxe blijven werken.
export function RotterdamWijkenLuxe() {
  return <StadWijkenLuxe stad={{ slug: 'rotterdam', naam: 'Rotterdam' }} />;
}
